import React, { ReactNode, useState } from 'react';
import { debounce } from 'lodash';
import { TableCell } from '@components/Table/components/TableCell';
import { TableEmpty } from '@components/Table/components/TableEmpty';
import { TableHeading } from '@components/Table/components/TableHeading';
import { TableRow } from '@components/Table/components/TableRow';
import { TableSorting } from '@components/Table/components/TableSorting';
import { ITableProps } from '@components/Table/types';
import { useLanguageContext } from '@hooks/useLanguageContext';
import { convertToDashes } from '@helpers/convertToDashes';
import { keyFormatter } from '@helpers/keyFormatter';

import './styles.scss';

export const Table = <T extends Record<string, unknown>>({
  tableSettings,
  tableData,
  tableLegend,
  actions,
  setSort,
  dictionaryKey,
  emptyText = '',
  dataLength,
  refreshFunction,
  next,
  isLastPage,
  search,
}: ITableProps<T>) => {
  const isEmpty = !tableData?.length && !search;

  const isSearchResultEmpty = !tableData?.length && search;

  const { dictionary } = useLanguageContext();

  const tableHeader = tableSettings.reduce<ReactNode[]>(
    (acc, { title, isSortable, accessor }) => [
      ...acc,
      <TableHeading
        key={`table_heading_${keyFormatter(accessor.toString())}`}
        className={`is-heading table-cell-${convertToDashes(accessor.toString())}${isSortable ? ' is-sortable' : ''}`}
      >
        {dictionary[dictionaryKey].table[accessor]}
        {isSortable && (
          <TableSorting
            setSort={setSort}
            column={String(accessor).replace(/[A-Z]/g, (name) => `_${name.toLowerCase()}`)}
          />
        )}
      </TableHeading>,
    ],
    [],
  );

  const parseDateTime = (dateStr?: string, timeStr?: string) => {
    if (!dateStr || !timeStr) return new Date(0);

    const timePart = timeStr.split(' - ')[0];

    const dateTimeStr = `${dateStr} ${timePart}`;

    return new Date(dateTimeStr);
  };

  const sortedTableData = tableData
    ? [...tableData].sort((a, b) => {
        const nameA = (a.name as string)?.toLowerCase?.() ?? '';

        const nameB = (b.name as string)?.toLowerCase?.() ?? '';

        const hasDateAndTimeA = a?.date && a?.time;

        const hasDateAndTimeB = b?.date && b?.time;

        if (hasDateAndTimeA && hasDateAndTimeB) {
          const dateTimeA = parseDateTime(a.date as string, a.time as string).getTime();

          const dateTimeB = parseDateTime(b.date as string, b.time as string).getTime();

          return dateTimeB - dateTimeA;
        }

        return nameA.localeCompare(nameB);
      })
    : [];

  const tableBody = sortedTableData?.reduce<ReactNode[]>((acc, current, rowIndex) => {
    const row = tableSettings.map((column, cellIndex) => {
      const currentCell = current[column.accessor];

      const cellClassName = `table-cell-${convertToDashes(column.accessor.toString())}`;

      if (column.formatter) {
        return (
          <TableCell key={`table_row_${rowIndex}_${cellIndex}`} className={cellClassName}>
            {column.formatter(current, column.accessor.toString())}
          </TableCell>
        );
      }

      if (!currentCell) {
        return (
          <TableCell key={`table_row_${rowIndex}_${cellIndex}`} className={cellClassName}>
            <span className="table-cell-placeholder">-</span>
          </TableCell>
        );
      }

      return (
        <TableCell key={`table_row_${rowIndex}_${cellIndex}`} className={cellClassName}>
          {String(currentCell)}
        </TableCell>
      );
    });

    return [
      ...acc,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      <TableRow key={`table_row_${rowIndex}`} row={current} actions={actions}>
        {row}
      </TableRow>,
    ];
  }, []);

  const colAmount = actions ? tableSettings.length + 1 : tableSettings.length;

  const tableClassName = `table ${isEmpty || isSearchResultEmpty ? 'is-empty' : ''}`;

  const [isFetching, setIsFetching] = useState(false);

  const handleScroll = (event: any) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;

    const endScroll = scrollTop + clientHeight >= scrollHeight - 50;

    if (!isLastPage && endScroll && !isFetching) {
      setIsFetching(true);
      next();
      setIsFetching(false);
    }
  };

  const debouncedHandleScroll = debounce(handleScroll, 50);

  return !!dataLength && !!refreshFunction && !!next ? (
    <div className={tableClassName} onScroll={debouncedHandleScroll}>
      {tableLegend}
      <table className="table-content">
        <thead className="table-header">
          <TableRow>
            {tableHeader}
            {actions && (
              <TableHeading isSticky className="table-actions">
                {dictionary.table.actions}
              </TableHeading>
            )}
          </TableRow>
        </thead>
        <tbody className="table-body">
          {!isEmpty && tableBody}
          {isEmpty && (
            <TableRow>
              <td colSpan={colAmount}>
                <TableEmpty />
              </td>
            </TableRow>
          )}
          {isSearchResultEmpty && (
            <TableRow>
              <td colSpan={colAmount}>
                <TableEmpty emptyType="search" />
              </td>
            </TableRow>
          )}
        </tbody>
      </table>
      <div className="table-placeholder">
        {isEmpty && <TableEmpty emptyText={emptyText} />}
        {isSearchResultEmpty && <TableEmpty emptyType="search" />}
      </div>
    </div>
  ) : (
    <div className={tableClassName}>
      {tableLegend}
      <table className="table-content">
        <thead className="table-header">
          <TableRow>
            {tableHeader}
            {actions && (
              <TableHeading isSticky className="table-actions">
                {dictionary.table.actions}
              </TableHeading>
            )}
          </TableRow>
        </thead>
        <tbody className="table-body">
          {!isEmpty && tableBody}
          {isEmpty && (
            <TableRow>
              <td colSpan={colAmount}>
                <TableEmpty />
              </td>
            </TableRow>
          )}
          {isSearchResultEmpty && (
            <TableRow>
              <td colSpan={colAmount}>
                <TableEmpty emptyType="search" />
              </td>
            </TableRow>
          )}
        </tbody>
      </table>
      <div className="table-placeholder">
        {isEmpty && <TableEmpty emptyText={emptyText} />}
        {isSearchResultEmpty && <TableEmpty emptyType="search" />}
      </div>
    </div>
  );
};
